<script setup lang="ts">
import { Divider } from "~/components/clarity-ui/Divider";
import { Heading } from "~/components/clarity-ui/Heading";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import CheckInComponent from "~/templates/components/shop/account/CheckInComponent.vue";
import {
  containsExchangeBoxProduct,
  getLineItemsWithoutPromotion,
  containsSubscriptionProduct,
} from "~/utils/helper/shop/cart";
import { useCartInformation } from "~/composables/shop/useCartInformation";
import SvgIcon from "~/templates/elements/SvgIcon.vue";

const { trackGuestCheckout } = useCheckoutTracking();
const { count } = useCartInformation();
const emit = defineEmits<{
  (e: "guest"): void;
  (e: "loadingStart"): void;
  (e: "loadingEnd"): void;
}>();

const { cartItems } = useCart();
const { isDesktop } = useDevice();

onMounted(() => {
  emit("loadingEnd");
});
</script>

<template>
  <div class="grid xl:grid-cols-3">
    <div class="w-full xl:hidden">
      <button class="text-icon-link" @click="$router.go(-1)">
        <SvgIcon class="h-3 w-3 -rotate-90" name="Arrow-Up" />
        <CommonLabel path="BWF/shop/shared/backToShop" />
      </button>

      <Divider class="mb-6 mt-4 xl:my-12" />
    </div>
    <div class="mx-auto flex flex-col gap-6 xl:col-span-2 xl:w-1/2 xl:gap-7">
      <CheckInComponent />

      <Divider class="my-3" />

      <Heading size="medium" tag="h2">
        <CommonLabel path="BWF/shop/checkout/mybrita/guestProceed" />
      </Heading>
      <div
        v-if="containsExchangeBoxProduct(cartItems) || containsSubscriptionProduct(cartItems)"
        class="notification"
        data-type="hint"
      >
        <SvgIcon class="notification-icon" name="Error-Circle" />
        <div>
          <CommonLabel
            class="font-medium"
            path="BWF/shop/checkout/mybrita/myBritaOnlyHeadline"
            tag="div"
          />
          <CommonLabel path="BWF/shop/checkout/mybrita/myBritaOnlyDescription" />
        </div>
      </div>
      <template v-else>
        <CommonLabel path="BWF/shop/checkout/mybrita/notReadyToRegister" />
        <button
          class="btn btn-ghost w-full"
          @click="
            $emit('guest');
            trackGuestCheckout();
          "
        >
          <CommonLabel path="BWF/shop/checkout/cart/toCheckout" />
        </button>
      </template>

      <CommonLabel class="mt-14" path="BWF/shared/vatFootnote" tag="div" />
    </div>
    <div>
      <div v-if="isDesktop" class="grid gap-8">
        <CheckoutSummary />
        <div class="grid gap-4">
          <div class="flex items-end justify-between">
            <Heading size="medium" tag="h2">
              <CommonLabel path="BWF/shop/shared/cartHeadline" />
            </Heading>
            <div v-if="cartItems.length > 0" class="text-sm">
              <CommonLabel class="font-medium" path="BWF/shop/shared/tableQuantity" />&nbsp;{{
                count
              }}
            </div>
          </div>
          <ul class="-my-4 divide-y divide-gray-200 pl-0" role="list">
            <li
              v-for="cartItem in getLineItemsWithoutPromotion(cartItems)"
              :key="cartItem.id"
              class="flex py-6"
            >
              <CheckoutCartItem :cart-item="cartItem" :child-variant="true" />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
